import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useVideoEditorPlaybackTimeContext } from 'containers/VideoEditor';
import { addingToTrack } from 'redux/modules/embed/actions';
import { splitLayoutSegment } from 'redux/modules/embed/actions/layout';
import { trackTypeSelector } from 'redux/modules/embed/selectors';
import { pushModal } from 'redux/modules/modal';
import { Dispatch } from 'redux/types';
import { secToMillis } from 'utils/time';
import { autoframedVideoIdAtPositionSelector } from './selectors';
import { AddAssetButtonResult } from './types';

export type UseAddAsssetButtonProps = {
  trackId: string;
};

export default function useAddAssetButton({
  trackId,
}: UseAddAsssetButtonProps): AddAssetButtonResult {
  const dispatch = useDispatch<Dispatch>();

  const trackType = useSelector(trackTypeSelector(trackId));
  const { positionSec } = useVideoEditorPlaybackTimeContext();
  const positionMillis = secToMillis(positionSec);

  const autoframedVideoIdAtPosition = useSelector(state =>
    autoframedVideoIdAtPositionSelector(state, trackId, positionMillis),
  );

  const action = autoframedVideoIdAtPosition ? 'split' : 'add';

  const handleClick = useCallback(() => {
    dispatch(addingToTrack(trackId));

    if (trackType === 'text') {
      dispatch(pushModal({ name: 'AddTextModal' }));
    }

    if (trackType === 'media') {
      if (action === 'add') {
        dispatch(pushModal({ name: 'AddMediaModal' }));
      } else {
        dispatch(splitLayoutSegment(autoframedVideoIdAtPosition, positionSec));
      }
    }

    if (trackType === 'audio') {
      dispatch(pushModal({ name: 'AddAudioModal' }));
    }

    if (trackType === 'waveform') {
      dispatch(pushModal({ name: 'AddWaveformModal' }));
    }
  }, [
    autoframedVideoIdAtPosition,
    action,
    dispatch,
    positionSec,
    trackId,
    trackType,
  ]);

  return {
    handleClick,
    action,
  };
}
